'use client'

import { Tabs } from '@/app/components/atoms/Tabs'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { cx } from '@/app/lib/utils'

import {
  MIN_SECTIONS_TO_RENDER,
  SECTION_NAV_ARIA_LABEL,
  SECTION_NAV_COMPONENT_ID,
  SECTION_NAV_COMPONENT_LABEL,
  SECTION_NAV_MODULE_NAME,
  SECTION_NAV_MODULE_POSITION,
  SECTION_NAV_MODULE_TYPE,
} from './constants'
import { useSectionNav } from './hooks'
import styles from './PerformanceSectionNav.module.scss'
import type { PerformanceSectionNavProps } from './types'

/**
 * `PerformanceSectionNav` [section] — barra de navegación por secciones de la
 * ficha de evento (Figma `5900:33036`; desktop `5900:35475`, mobile
 * `5900:40481`).
 *
 * Se ancla al borde inferior del `<header>` sticky y aparece cuando la banda
 * «Compártelo en» del hero deja de verse; desde ahí acompaña al usuario todo el
 * resto de la ficha marcando en qué sección está.
 *
 * **Navega sin hipervínculos**: botones + `scrollIntoView`, igual que el índice
 * A–Z de Artistas/Recintos (`AlphaListing`). Por eso reutiliza el átomo `Tabs`
 * en modo `pattern="navigation"` —`<nav>` + `aria-current`, sin `role="tab"`—:
 * no hay `tabpanel` que anunciar, solo un destino al que llevar el scroll.
 *
 * `position: fixed` y no `sticky`: la barra no puede reservar hueco en el flujo
 * mientras está oculta, y la regla de stickies del repo exige que un sticky
 * viva en un contenedor tan alto como su rango de scroll — aquí no lo hay.
 *
 * Client Component: mide el scroll y gestiona el foco/eventos de las pestañas.
 * Cada selección dispara `ui_interaction` (`component_type: 'tab'`) — ver
 * `constants.ts` para el porqué de `ui_interaction` y no `click`.
 */
export function PerformanceSectionNav({ shareBarId, heroId }: PerformanceSectionNavProps) {
  const { items, visible, activeId, handleSelect } = useSectionNav({ shareBarId, heroId })
  const analytics = useOptionalPageAnalytics()

  if (items.length < MIN_SECTIONS_TO_RENDER || activeId === undefined) return null

  function handleTabSelect(id: string) {
    const label = items.find((item) => item.id === id)?.label ?? id
    analytics?.trackUiInteraction({
      component_type: 'tab',
      component_action: 'select',
      component_id: SECTION_NAV_COMPONENT_ID,
      component_label: SECTION_NAV_COMPONENT_LABEL,
      selected_value: slugify(label),
      module_name: SECTION_NAV_MODULE_NAME,
      module_type: SECTION_NAV_MODULE_TYPE,
      module_position: SECTION_NAV_MODULE_POSITION,
    })
    handleSelect(id)
  }

  return (
    <div
      className={cx(styles.root, visible && styles.visible)}
      aria-hidden={!visible}
      // `aria-hidden` no saca del orden de tabulación: sin `inert`, la barra
      // oculta seguiría siendo alcanzable con Tab (mismo criterio que
      // `PerformanceBuyBox`).
      inert={!visible}
    >
      <Tabs
        pattern="navigation"
        tone="inverse"
        width="intrinsic"
        ariaLabel={SECTION_NAV_ARIA_LABEL}
        className={styles.tabs}
        tabs={items.map((item) => ({ value: item.id, label: item.label }))}
        value={activeId}
        onChange={handleTabSelect}
      />
    </div>
  )
}
